import { useId } from 'react';
import { cascadeAdministrativeArea, getAdministrativeDivisionLabel, getAdministrativeDivisions } from '@quds/shared';
import { FormField, Input, fieldDescribedBy } from '@/components/ui';
import { AdministrativeDivisionSelect } from './AdministrativeDivisionSelect';
import { CountrySelect } from './CountrySelect';

export interface LocationFieldValues {
  country: string;
  administrativeAreaLevel1: string;
  city: string;
  area: string;
  address: string;
}

export type LocationFieldErrors = Partial<Record<keyof LocationFieldValues, string>>;

export interface LocationFieldsProps {
  values: LocationFieldValues;
  errors?: LocationFieldErrors;
  onChange: (patch: Partial<LocationFieldValues>) => void;
  /** Show optional area / neighborhood field. Default true. */
  showArea?: boolean;
  /** Show street address. Default true. */
  showAddress?: boolean;
  required?: { country?: boolean; city?: boolean; division?: boolean };
  className?: string;
  disabled?: boolean;
}

/**
 * Cascading location fields: Country → Division → City → Area? → Address.
 * Clears (or re-cascades) division when country changes.
 */
export function LocationFields({
  values,
  errors = {},
  onChange,
  showArea = true,
  showAddress = true,
  required = { country: true, city: true },
  className,
  disabled,
}: LocationFieldsProps) {
  const base = useId();
  const id = (field: keyof LocationFieldValues) => `${base}-${field}`;
  const described = (field: keyof LocationFieldValues, hint?: string) =>
    fieldDescribedBy(id(field), { hint, error: errors[field] });

  const divisions = getAdministrativeDivisions(values.country);
  const divisionLabel = getAdministrativeDivisionLabel(values.country);
  const cityPlaceholder = divisions[0] ? `e.g. ${divisions[0].name}` : 'City';

  const onCountryChange = (country: string) => {
    const nextDivision = cascadeAdministrativeArea(country, values.administrativeAreaLevel1) ?? '';
    onChange({
      country,
      administrativeAreaLevel1: nextDivision,
    });
  };

  return (
    <div className={className ?? 'contents'}>
      <FormField label="Country" htmlFor={id('country')} required={required.country} error={errors.country}>
        <CountrySelect
          id={id('country')}
          value={values.country}
          onChange={onCountryChange}
          invalid={!!errors.country}
          disabled={disabled}
          aria-describedby={described('country')}
        />
      </FormField>

      {divisions.length > 0 && (
        <FormField
          label={divisionLabel}
          htmlFor={id('administrativeAreaLevel1')}
          required={required.division}
          error={errors.administrativeAreaLevel1}
        >
          <AdministrativeDivisionSelect
            id={id('administrativeAreaLevel1')}
            country={values.country}
            value={values.administrativeAreaLevel1}
            onChange={(administrativeAreaLevel1) => onChange({ administrativeAreaLevel1 })}
            invalid={!!errors.administrativeAreaLevel1}
            disabled={disabled}
            aria-describedby={described('administrativeAreaLevel1')}
            hideWhenEmpty={false}
          />
        </FormField>
      )}

      <FormField label="City" htmlFor={id('city')} required={required.city} error={errors.city}>
        <Input
          id={id('city')}
          value={values.city}
          onChange={(e) => onChange({ city: e.target.value })}
          placeholder={cityPlaceholder}
          list={`${base}-cities`}
          invalid={!!errors.city}
          disabled={disabled}
          aria-describedby={described('city')}
          autoComplete="address-level2"
          maxLength={80}
        />
        <datalist id={`${base}-cities`}>
          {divisions.map((d) => (
            <option key={d.code} value={d.name} />
          ))}
        </datalist>
      </FormField>

      {showArea && (
        <FormField label="Area" htmlFor={id('area')} error={errors.area} hint="Neighborhood or district (optional)">
          <Input
            id={id('area')}
            value={values.area}
            onChange={(e) => onChange({ area: e.target.value })}
            placeholder="Optional"
            invalid={!!errors.area}
            disabled={disabled}
            aria-describedby={described('area', 'hint')}
            maxLength={120}
          />
        </FormField>
      )}

      {showAddress && (
        <FormField label="Address" htmlFor={id('address')} error={errors.address} className="sm:col-span-2">
          <Input
            id={id('address')}
            value={values.address}
            onChange={(e) => onChange({ address: e.target.value })}
            placeholder="Street, building (optional)"
            invalid={!!errors.address}
            disabled={disabled}
            aria-describedby={described('address')}
            maxLength={255}
            autoComplete="street-address"
          />
        </FormField>
      )}
    </div>
  );
}
